import { FormProvider, getFormProps } from '@conform-to/react'
import { Button, Flex, Heading, Text, Link as RLink } from '@radix-ui/themes'
import { Link } from '@remix-run/react'
import * as React from 'react'
import { z } from 'zod'
import {
	FormDefaults,
	FormDefaultsHiddenField,
} from '#app/components/common/form-defaults.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import { Checkbox } from '#app/components/ui/checkbox.tsx'
import {
	TextField,
	type TextFieldProps,
} from '#app/components/ui/text-field.tsx'
import { type Locale } from '#app/models/locale.ts'
import { REGISTRATION_SEARCH_PARAMS } from '#app/routes/($lang)._auth.registration/route-config'
import { type App } from '#app/routes/_layout/component.route.tsx'
import {
	ROOT_APP_VIEW,
	type FormEnum,
} from '#app/routes/_layout/route-config.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useRegistrationForm } from '#app/utils/hooks/forms/use-registration-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Registration form
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	firstName = 'firstName',
	lastName = 'lastName',
	email = 'email',
	password = 'password',
	subscribedToNewsletter = 'subscribedToNewsletter',
	inviteId = 'inviteId',
}

const FORM: FormEnum = {
	id: 'registration-form',
	action: '/registration',
	fetcherKey: 'registration',
	subaction: 'registration',
	redirect: '/login',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.firstName]: z
			.string({ required_error: 'validation.firstName.required' })
			.min(2, 'validation.firstName.min')
			.max(255, 'validation.firstName.max'),
		[INPUTS.lastName]: z
			.string({ required_error: 'validation.lastName.required' })
			.min(2, 'validation.lastName.min')
			.max(255, 'validation.lastName.max'),
		[INPUTS.email]: z
			.string({ required_error: 'validation.email.required' })
			.email('validation.email.email')
			.max(254, 'validation.email.max'),
		[INPUTS.password]: z
			.string({ required_error: 'validation.password.required' })
			.min(4, 'validation.password.min')
			.max(254, 'validation.password.max'),
		[INPUTS.subscribedToNewsletter]: z.boolean().optional(),
		[INPUTS.inviteId]: z.string().optional(),
	},
})

type RegistrationFormProps = {
	app: App
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
	app,
	locale,
	setApp,
}) => {
	const {
		registrationFetcher,
		registrationFormValues,
		registrationForm,
		registrationFields,
		t,
		searchParams,
	} = useRegistrationForm({ app })

	return (
		<FormProvider context={registrationForm.context}>
			<registrationFetcher.Form
				method="post"
				action={`/${locale}${FORM.action}?${searchParams}`}
				{...getFormProps(registrationForm)}
				{...(app.options?.preventScrollReset && { preventScrollReset: true })}
			>
				<FormDefaults
					form={registrationForm}
					fields={registrationFields}
					values={registrationFormValues}
				/>
				{searchParams.has(REGISTRATION_SEARCH_PARAMS.inviteId) && (
					<FormDefaultsHiddenField
						meta={registrationFields[REGISTRATION_SEARCH_PARAMS.inviteId]}
					/>
				)}
				<Heading align="center" size="8" color="green">
					{t('common.title')}
				</Heading>
				<Flex direction="column" gapY="6" mt="6">
					{[
						{
							title: INPUTS.firstName,
						},
						{
							title: INPUTS.lastName,
						},
						{
							title: INPUTS.email,
							type: 'email',
						},
						{ title: INPUTS.password, type: 'password' },
					].map(({ title, type = 'text' }) => (
						<TextField
							key={title}
							type={type as TextFieldProps['type']}
							meta={
								// @ts-ignore
								registrationFields[title]
							}
							t={t}
						>
							{t(`label.${title}`)}
						</TextField>
					))}
					<Checkbox
						meta={registrationFields[INPUTS.subscribedToNewsletter]}
						t={t}
					>
						{t('label.subscribeToNewsletter')}
					</Checkbox>
					<Flex direction="column" gapY="3" mt="3">
						<Flex justify="end">
							<Link
								to={`/${locale}/reset-password${registrationFields[INPUTS['email']].value ? `?email=${registrationFields[INPUTS['email']].value}` : ''}`}
								prefetch="viewport"
								onClick={(e) => {
									if (app.displayModal) {
										e.preventDefault()
										setApp((prev) => ({
											...prev,
											modalView: ROOT_APP_VIEW.resetPasswordView,
											options: {
												...prev.options,
												data: {
													[INPUTS.email]:
														registrationFields[INPUTS['email']].value ||
														('' as string),
												},
											},
										}))
									}
								}}
							>
								<RLink asChild weight="bold" size="2">
									<span>{t('button.resetPassword')}</span>
								</RLink>
							</Link>
						</Flex>
						<Button size="3" type="submit">
							<SubmissionButton fetcher={registrationFetcher}>
								{t('button.registrationSubmit')}
							</SubmissionButton>
						</Button>
						<Flex align="center" justify="center" gapX="2" mt="4">
							<Text color="gray">{t('common.login')}</Text>
							<Link
								to={`/${locale}/login${registrationFields[INPUTS['email']].value ? `?email=${registrationFields[INPUTS['email']].value}` : ''}`}
								prefetch="viewport"
								onClick={(e) => {
									if (app.displayModal) {
										e.preventDefault()
										setApp((prev) => ({
											...prev,
											modalView: ROOT_APP_VIEW.loginView,
											options: {
												...prev.options,
												data: {
													[INPUTS.email]:
														registrationFields[INPUTS['email']].value ||
														('' as string),
												},
											},
										}))
									}
								}}
							>
								<RLink asChild weight="bold" size="2">
									<span>{t('button.login')}</span>
								</RLink>
							</Link>
						</Flex>
					</Flex>
				</Flex>
			</registrationFetcher.Form>
		</FormProvider>
	)
}
RegistrationForm.displayName = 'RegistrationForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type RegistrationFormProps,
	RegistrationForm,
	INPUTS as REGISTRATION_INPUTS,
	FORM as REGISTRATION_FORM,
	schema as registrationSchema,
}
